import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import ExerciseScreen from './pages/ExerciseScreen';
import AdminPanel from './pages/AdminPanel';
import CategoryPage from './pages/CategoryPage';
import ExercisesPage from './pages/ExercisesPage';
import MyExercisesPage from './pages/MyExercisesPage';
import StatsPage from './pages/StatsPage';
import ProfilePage from './pages/ProfilePage';
import SidebarNav from './components/SidebarNav';
import MetronomePage from './pages/MetronomePage';
import SubcategoryPage from './pages/SubcategoryPage';
import ProgramsPage from './pages/ProgramsPage';
import UpgradePage from './pages/UpgradePage';
import './index.css';

// Routes accessible to free users (no trial needed)
const FREE_ROUTES = ['/metronome', '/profile', '/upgrade'];

function PrivateRoute({ children }) {
  const { user, loading, profile, hasAccess } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (!profile) return <LoadingScreen />;
  if (!hasAccess && !FREE_ROUTES.includes(location.pathname)) {
    return <Navigate to="/upgrade" />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return !user ? children : <Navigate to="/" />;
}

function LoadingScreen() {
  return (
    <div style={{ minHeight:'100vh', background:'#111009', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <img src="/logo.png" alt="Drumnasium" style={{ width:80, height:80, objectFit:'contain', marginBottom:8 }}/>
      <div style={{ width:40, height:40, borderRadius:'50%', border:'3px solid #2A2520', borderTopColor:'#FF5500', animation:'spin 0.8s linear infinite' }}/>
    </div>
  );
}

function AppLayout() {
  const { user, loading, hasAccess } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen/>;
  // Only show sidebar on full-access routes
  const showSidebar = user && hasAccess && !FREE_ROUTES.includes(location.pathname);
  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#111009' }}>
      {showSidebar && <SidebarNav/>}
      <div style={{ flex:1 }} className={showSidebar ? 'app-content' : ''}>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/upgrade" element={<PrivateRoute><UpgradePage /></PrivateRoute>} />
          <Route path="/metronome" element={<PrivateRoute><MetronomePage /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/category/:id" element={<PrivateRoute><CategoryPage /></PrivateRoute>} />
          <Route path="/subcategory/:id" element={<PrivateRoute><SubcategoryPage /></PrivateRoute>} />
          <Route path="/exercise/:id" element={<PrivateRoute><ExerciseScreen /></PrivateRoute>} />
          <Route path="/programs" element={<PrivateRoute><ProgramsPage /></PrivateRoute>} />
          <Route path="/exercises" element={<PrivateRoute><ExercisesPage /></PrivateRoute>} />
          <Route path="/my-exercises" element={<PrivateRoute><MyExercisesPage /></PrivateRoute>} />
          <Route path="/stats" element={<PrivateRoute><StatsPage /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><AdminPanel /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppLayout/>
      </BrowserRouter>
    </AuthProvider>
  );
}
